import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Lightbulb,
  Plus,
  Search,
  Filter,
  Archive,
  LogOut,
  User,
  TrendingUp,
  CheckCircle2,
  Clock,
  MoreVertical,
  Edit,
  Trash2,
  ArchiveRestore,
  X,
  Star,
  AlertCircle,
  Moon,
  Sun,
  BarChart3,
  Flag,
  MessageCircle,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isPast } from 'date-fns';
import { useTheme } from '../contexts/ThemeContext';
import { KeyboardShortcutsProvider } from '../contexts/KeyboardContext';
import CommentsModal from '../components/CommentsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Work', 'Personal', 'Projects', 'Innovation', 'Research', 'Design', 'Other'];
const PROGRESS_STATUSES = ['Not Started', 'In Progress', 'Completed'];
const PRIORITIES = ['Low', 'Medium', 'High'];

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const searchInputRef = React.useRef(null);
  
  const [ideas, setIdeas] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedIdeaForComments, setSelectedIdeaForComments] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Work',
    tags: '',
    progress_status: 'Not Started',
    progress_percentage: 0,
    assigned_to: '',
    priority: 'Medium',
    due_date: '',
    is_favorite: false
  });

  useEffect(() => {
    fetchData();
  }, [searchQuery, filterCategory, filterTag, filterAssignee, filterPriority, showArchived, showFavoritesOnly]);

  const fetchData = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (filterCategory) params.category = filterCategory;
      if (filterTag) params.tag = filterTag;
      if (filterAssignee) params.assigned_to = filterAssignee;
      if (filterPriority) params.priority = filterPriority;
      if (showFavoritesOnly) params.is_favorite = true;
      params.archived = showArchived;

      const [ideasRes, usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/ideas`, { params }),
        axios.get(`${API}/users`),
        axios.get(`${API}/stats`)
      ]);

      setIdeas(ideasRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        assigned_to: formData.assigned_to || null
      };

      if (editingIdea) {
        await axios.put(`${API}/ideas/${editingIdea.id}`, payload);
        toast.success('Idea updated successfully');
      } else {
        await axios.post(`${API}/ideas`, payload);
        toast.success('Idea created successfully');
      }

      setShowModal(false);
      setEditingIdea(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save idea');
    }
  };

  const handleEdit = (idea) => {
    setEditingIdea(idea);
    setFormData({
      title: idea.title,
      description: idea.description,
      category: idea.category,
      tags: idea.tags.join(', '),
      progress_status: idea.progress_status,
      progress_percentage: idea.progress_percentage,
      assigned_to: idea.assigned_to || '',
      priority: idea.priority || 'Medium',
      due_date: idea.due_date || '',
      is_favorite: idea.is_favorite || false
    });
    setShowModal(true);
    setActiveDropdown(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this idea?')) return;
    
    try {
      await axios.delete(`${API}/ideas/${id}`);
      toast.success('Idea deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete idea');
    }
    setActiveDropdown(null);
  };

  const handleArchive = async (idea) => {
    try {
      await axios.put(`${API}/ideas/${idea.id}`, { archived: !idea.archived });
      toast.success(idea.archived ? 'Idea restored' : 'Idea archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to update idea');
    }
    setActiveDropdown(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Work',
      tags: '',
      progress_status: 'Not Started',
      progress_percentage: 0,
      assigned_to: '',
      priority: 'Medium',
      due_date: '',
      is_favorite: false
    });
  };

  const toggleFavorite = async (idea, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/ideas/${idea.id}`, { is_favorite: !idea.is_favorite });
      fetchData();
      toast.success(idea.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const openComments = (idea, e) => {
    e.stopPropagation();
    setSelectedIdeaForComments(idea);
    setShowCommentsModal(true);
  };

  const closeComments = () => {
    setShowCommentsModal(false);
    setSelectedIdeaForComments(null);
  };

  const handleCommentAdded = () => {
    fetchData(); // Refresh to update comment counts
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'In Progress': return 'bg-blue-100 text-blue-900 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      default: return 'bg-zinc-100 text-zinc-900 border-zinc-200 dark:bg-zinc-700 dark:text-zinc-300 dark:border-zinc-600';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-900 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      case 'Low': return 'bg-green-100 text-green-900 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      default: return 'bg-yellow-100 text-yellow-900 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return isPast(parseISO(dueDate)) && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const allTags = [...new Set(ideas.flatMap(idea => idea.tags))];

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <KeyboardShortcutsProvider
      onNewIdea={() => { setShowModal(true); setEditingIdea(null); resetForm(); }}
      onSearch={() => searchInputRef.current?.focus()}
      onClose={() => setShowModal(false)}
    >
      <div className="min-h-screen bg-[#FAFAF9] dark:bg-zinc-900">
        {/* Header */}
        <header className="sticky top-0 z-40 w-full border-b border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-zinc-800/60">
          <div className="container mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <img src="/gba-logo.png" alt="GBA Logo" className="h-8 sm:h-9 w-auto" />
              <span className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-white hidden sm:block">GBA Idea App</span>
              <span className="text-base font-bold text-zinc-900 dark:text-white sm:hidden">GBA</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
              {user.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  data-testid="admin-dashboard-button"
                  className="flex items-center gap-1.5 bg-[#C8E6C9] dark:bg-[#1B5E20]/30 text-[#1B5E20] dark:text-[#8BC34A] hover:bg-[#A5D6A7] dark:hover:bg-[#1B5E20]/50 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors border border-[#A5D6A7] dark:border-[#2E7D32]"
                >
                  <Shield size={14} className="sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              <button
                onClick={() => navigate('/analytics')}
                data-testid="analytics-button"
                className="flex items-center gap-1.5 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors"
              >
                <BarChart3 size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Analytics</span>
              </button>
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                data-testid="toggle-favorites-button"
                className={`flex items-center gap-1.5 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors ${
                  showFavoritesOnly
                    ? 'bg-[#C8E6C9] dark:bg-[#1B5E20]/30 text-[#1B5E20] dark:text-[#8BC34A]'
                    : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white'
                }`}
              >
                <Star size={14} className="sm:w-4 sm:h-4" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
                <span className="hidden lg:inline">Favorites</span>
              </button>
              <button
                onClick={() => setShowArchived(!showArchived)}
                data-testid="toggle-archived-button"
                className={`flex items-center gap-1.5 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors ${
                  showArchived
                    ? 'bg-[#C8E6C9] dark:bg-[#1B5E20]/30 text-[#1B5E20] dark:text-[#8BC34A]'
                    : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white'
                }`}
              >
                <Archive size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden lg:inline">{showArchived ? 'Archived' : 'Active'}</span>
              </button>
              <button
                onClick={toggleTheme}
                data-testid="toggle-theme-button"
                className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-1.5 sm:p-2 transition-colors"
                title={isDark ? 'Light mode' : 'Dark mode'}
              >
                {isDark ? <Sun size={16} className="sm:w-[18px] sm:h-[18px]" /> : <Moon size={16} className="sm:w-[18px] sm:h-[18px]" />}
              </button>
              <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg border border-zinc-200 dark:border-zinc-600">
                <User size={14} className="text-zinc-500 dark:text-zinc-400 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium text-zinc-900 dark:text-white hidden md:inline">{user.name}</span>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-1.5 sm:p-2 transition-colors"
                title="Logout"
              >
                <LogOut size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
          </div>
        </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-total-ideas">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Total</span>
              <Lightbulb size={16} className="text-zinc-400 dark:text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.total_ideas || 0}</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-in-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">In Progress</span>
              <Clock size={16} className="text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.in_progress || 0}</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-completed">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Completed</span>
              <CheckCircle2 size={16} className="text-green-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.completed || 0}</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-high-priority">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">High Priority</span>
              <Flag size={16} className="text-red-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.high_priority || 0}</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-favorites">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Favorites</span>
              <Star size={16} className="text-[#689F38]" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.favorites || 0}</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="stat-archived">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-mono">Archived</span>
              <Archive size={16} className="text-[#689F38]" />
            </div>
            <p className="text-2xl font-bold text-zinc-900 dark:text-white">{stats.archived_ideas || 0}</p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" size={18} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search ideas... (Press / to focus)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-ideas-input"
              className="w-full h-11 pl-10 pr-4 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-button"
            className="flex items-center gap-2 bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
          >
            <Filter size={18} />
            Filters
          </button>
          <button
            onClick={() => {
              setShowModal(true);
              setEditingIdea(null);
              resetForm();
            }}
            data-testid="create-idea-button"
            className="flex items-center gap-2 bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
          >
            <Plus size={18} />
            New Idea
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 mb-8 shadow-sm" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  data-testid="filter-category-select"
                  className="w-full h-10 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Priority</label>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  data-testid="filter-priority-select"
                  className="w-full h-10 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                >
                  <option value="">All Priorities</option>
                  {PRIORITIES.map(pri => (
                    <option key={pri} value={pri}>{pri}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Tag</label>
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  data-testid="filter-tag-select"
                  className="w-full h-10 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                >
                  <option value="">All Tags</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Assigned To</label>
                <select
                  value={filterAssignee}
                  onChange={(e) => setFilterAssignee(e.target.value)}
                  data-testid="filter-assignee-select"
                  className="w-full h-10 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                >
                  <option value="">All Team Members</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={() => {
                setFilterCategory('');
                setFilterPriority('');
                setFilterTag('');
                setFilterAssignee('');
              }}
              data-testid="clear-filters-button"
              className="mt-4 text-sm text-[#5B6B8F] dark:text-[#8BC34A] hover:text-[#4A5A7C] dark:hover:text-[#5B6B8F]-300 font-medium"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Ideas List */}
        <div className="space-y-4" data-testid="ideas-list">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Loading...</div>
          ) : ideas.length === 0 ? (
            <div className="text-center py-12">
              <Lightbulb className="mx-auto h-12 w-12 text-zinc-300 mb-4" />
              <p className="text-lg font-medium text-zinc-900 mb-2">No ideas yet</p>
              <p className="text-zinc-600 mb-6">Start capturing your ideas to see them here</p>
              <button
                onClick={() => {
                  setShowModal(true);
                  setEditingIdea(null);
                  resetForm();
                }}
                data-testid="empty-state-create-button"
                className="inline-flex items-center gap-2 bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
              >
                <Plus size={18} />
                Create your first idea
              </button>
            </div>
          ) : (
            ideas.map((idea) => (
              <div
                key={idea.id}
                data-testid={`idea-card-${idea.id}`}
                className={`bg-white dark:bg-zinc-800 border rounded-xl p-6 shadow-sm hover:shadow-md transition-all group ${
                  isOverdue(idea.due_date) ? 'border-red-300 dark:border-red-800' : 'border-zinc-200 dark:border-zinc-700'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <button
                      onClick={(e) => toggleFavorite(idea, e)}
                      data-testid={`favorite-${idea.id}`}
                      className="mt-1 text-zinc-400 hover:text-[#689F38] transition-colors"
                    >
                      <Star size={20} fill={idea.is_favorite ? 'currentColor' : 'none'} className={idea.is_favorite ? 'text-[#689F38]' : ''} />
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{idea.title}</h3>
                        {isOverdue(idea.due_date) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-400 border border-red-200 dark:border-red-800">
                            <AlertCircle size={12} />
                            Overdue
                          </span>
                        )}
                      </div>
                      {idea.description && (
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">{idea.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === idea.id ? null : idea.id)}
                      data-testid={`idea-menu-${idea.id}`}
                      className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg p-2 transition-colors"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {activeDropdown === idea.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg py-1 z-10" data-testid={`idea-dropdown-${idea.id}`}>
                        <button
                          onClick={() => handleEdit(idea)}
                          data-testid={`edit-idea-${idea.id}`}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleArchive(idea)}
                          data-testid={`archive-idea-${idea.id}`}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700"
                        >
                          {idea.archived ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                          {idea.archived ? 'Restore' : 'Archive'}
                        </button>
                        <button
                          onClick={() => handleDelete(idea.id)}
                          data-testid={`delete-idea-${idea.id}`}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border font-mono ${getPriorityColor(idea.priority)}`}>
                    {idea.priority}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-600 font-mono">
                    {idea.category}
                  </span>
                  {idea.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E8F5E9] dark:bg-[#1B5E20]/30 text-[#1B5E20] dark:text-[#8BC34A] border border-[#A5D6A7] dark:border-[#2E7D32] font-mono">
                      {tag}
                    </span>
                  ))}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getProgressColor(idea.progress_status)}`}>
                    {idea.progress_status}
                  </span>
                  {idea.due_date && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border font-mono ${
                      isOverdue(idea.due_date)
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-400 border-red-200 dark:border-red-800'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-400 border-blue-200 dark:border-blue-800'
                    }`}>
                      Due: {format(parseISO(idea.due_date), 'MMM d, yyyy')}
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-zinc-500 font-mono uppercase tracking-wide">Progress</span>
                    <span className="text-xs font-semibold text-zinc-900 font-mono">{idea.progress_percentage}%</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#5B6B8F] rounded-full transition-all duration-300"
                      style={{ width: `${idea.progress_percentage}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
                  <div className="flex items-center gap-4">
                    <span className="font-mono">Created by {idea.created_by_name}</span>
                    {idea.assigned_to_name && (
                      <span className="flex items-center gap-1 font-mono">
                        <User size={12} />
                        Assigned to {idea.assigned_to_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => openComments(idea, e)}
                      data-testid={`comments-button-${idea.id}`}
                      className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400 hover:text-[#5B6B8F] dark:hover:text-[#8BC34A] transition-colors"
                    >
                      <MessageCircle size={14} />
                      <span className="font-mono font-semibold">{idea.comment_count || 0}</span>
                    </button>
                    <span className="font-mono">{format(new Date(idea.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Comments Modal */}
      {showCommentsModal && selectedIdeaForComments && (
        <CommentsModal
          idea={selectedIdeaForComments}
          onClose={closeComments}
          onCommentAdded={handleCommentAdded}
        />
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="idea-modal"
          >
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-zinc-900">{editingIdea ? 'Edit Idea' : 'New Idea'}</h2>
              <button
                onClick={() => setShowModal(false)}
                data-testid="close-modal-button"
                className="text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg p-2 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-zinc-700 mb-1.5">
                  Title *
                </label>
                <input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="idea-title-input"
                  className="w-full h-11 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  placeholder="Enter idea title"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-zinc-700 mb-1.5">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  data-testid="idea-description-input"
                  className="w-full rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent resize-none"
                  placeholder="Describe your idea..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Category *
                  </label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    data-testid="idea-category-select"
                    className="w-full h-11 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Priority *
                  </label>
                  <select
                    id="priority"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    required
                    data-testid="idea-priority-select"
                    className="w-full h-11 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  >
                    {PRIORITIES.map(pri => (
                      <option key={pri} value={pri}>{pri}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Tags (comma-separated)
                  </label>
                  <input
                    id="tags"
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    data-testid="idea-tags-input"
                    className="w-full h-11 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2 text-sm text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                    placeholder="e.g., urgent, feature, brainstorm"
                  />
                </div>

                <div>
                  <label htmlFor="due_date" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                    Due Date
                  </label>
                  <input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    data-testid="idea-due-date-input"
                    className="w-full h-11 rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-4 py-2 text-sm text-zinc-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="progress_status" className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Progress Status
                  </label>
                  <select
                    id="progress_status"
                    value={formData.progress_status}
                    onChange={(e) => setFormData({ ...formData, progress_status: e.target.value })}
                    data-testid="idea-status-select"
                    className="w-full h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                  >
                    {PROGRESS_STATUSES.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="progress_percentage" className="block text-sm font-medium text-zinc-700 mb-1.5">
                    Progress: {formData.progress_percentage}%
                  </label>
                  <input
                    id="progress_percentage"
                    type="range"
                    min="0"
                    max="100"
                    value={formData.progress_percentage}
                    onChange={(e) => setFormData({ ...formData, progress_percentage: parseInt(e.target.value) })}
                    data-testid="idea-progress-slider"
                    className="w-full h-11 accent-[#5B6B8F]"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="assigned_to" className="block text-sm font-medium text-zinc-700 mb-1.5">
                  Assign To
                </label>
                <select
                  id="assigned_to"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  data-testid="idea-assignee-select"
                  className="w-full h-11 rounded-lg border border-zinc-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#689F38] focus-visible:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  data-testid="cancel-modal-button"
                  className="bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="save-idea-button"
                  className="bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
                >
                  {editingIdea ? 'Update Idea' : 'Create Idea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </KeyboardShortcutsProvider>
  );
};

export default Dashboard;